<template>
  <div id="scheduledShutdown">

    <PageTitle :title="$t('scheduled_shutdown.title')" :subtitle="$t('scheduled_shutdown.subtitle')">
      <button class="btn btn-primary" @click="newSchedule">{{$t('scheduled_shutdown.new')}}</button>
    </PageTitle>

    <PageContainer>

      <Empty v-if="schedules.length === 0" :text="$t('scheduled_shutdown.empty')" :button="{text: $t('scheduled_shutdown.new'), action: newSchedule}"></Empty>

      <table class="table" v-if="schedules.length > 0">
        <tr>
          <th class="icon"></th>
          <th class="start">{{$t('scheduled_shutdown.start_date')}}</th>
          <th class="end">{{$t('scheduled_shutdown.start_date')}}</th>
          <th class="companies">{{$t('scheduled_shutdown.companies')}}</th>
          <th class="action"></th>
        </tr>
        <tr v-for="(schedule, scheduleIndex) of schedules" :key="'schedule-' + scheduleIndex">
          <td class="icon">
            <div class="table__icon">
              <svgicon src="icon/calendar.svg" :height="16" :width="16"></svgicon>
            </div>
          </td>
          <td class="start">{{formatDate(schedule.start)}}</td>
          <td class="end">{{formatDate(schedule.end)}}</td>
          <td class="companies">
            <div>
              <Tag grey v-for="company of schedule.companies" :key="`schedule-${scheduleIndex}-company-${company.name}`">{{company.name}}</Tag>
            </div>
          </td>
          <td class="menu-items">
            <div class="actions">
              <ActionMenu icon="/icon/actions-horizontal.png">
                <div class="item" @click="editSchedule(schedule)">{{$t('edit')}}</div>
                <div class="item" @click="removeSchedule(scheduleIndex, schedule)">{{$t('remove')}}</div>
              </ActionMenu>
            </div>
          </td>
        </tr>
      </table>

    </PageContainer>

    <Modal name="schedule-edit" :title="$t('scheduled_shutdown.single')" :subtitle="$t('scheduled_shutdown.edit')"
           :actions="[{name: $t('back'), action: 'close'}, {name: $t('save'), action: saveSchedule, primary: true}]">
      <div v-if="selectedSchedule">
        <div class="group">
          <label>{{$t('scheduled_shutdown.start_date')}}</label>
          <datetime type="datetime" value-zone="local" v-model="selectedSchedule.start" input-style="width: 100%" />
        </div>
        <div class="group">
          <label>{{$t('scheduled_shutdown.end_date')}}</label>
          <datetime type="datetime" value-zone="local" v-model="selectedSchedule.end" input-style="width: 100%" />
        </div>
        <div class="group">
          <LocalizedInput textarea :title="$t('scheduled_shutdown.message')" :translate="selectedSchedule.closureMessage"/>
        </div>
        <ListMultiSelect :bank="branchCompanies" :selected="selectedSchedule.companies"
                         show-select-all show-unselect-all
                         :bank-title="$t('companies.title')"
                         :selected-title="$t('scheduled_shutdown.companies')"
                         comparisonKey="id"></ListMultiSelect>
      </div>
    </Modal>

  </div>
</template>

<script>
  import PageTitle from "@/components/PageTitle";
  import PageContainer from "@/components/PageContainer";
  import ActionMenu from "@/components/tools/ActionMenu";
  import Empty from "@/components/tools/Empty";
  import Tag from "@/components/tools/Tag";
  import ListMultiSelect from "@/components/tools/ListMultiSelect";
  import LocalizedInput from "@/components/tools/LocalizedInput";
  import moment from "moment";
  import {mapActions} from "vuex";
  import {Core} from "@/core/Core";

  export default {
    name: "ScheduledShutdown",

    components: { PageTitle, PageContainer, ActionMenu, Empty, Tag, ListMultiSelect, LocalizedInput },

    data() {
      return {
        selectedSchedule: null,
        branch: null,
        branchCompanies: [],
        dateFormat: "lll",
        apiDateFormat: "YYYY-MM-DD HH:mm:ss",
        schedules: []
      }
    },

    async mounted() {
      this.$store.state.pageTitleTranslationKey = "scheduled_shutdown.title";
      this.$store.state.pageIcon = "/icon/calendar.svg";

      showSpinner();
      this.branch = await this.$store.getters.getContextBranch;
      await this.loadBranchCompanies();
      await this.getSchedules();
      hideSpinner();
    },

    methods: {
      newSchedule() {
        this.selectedSchedule = {
          branch: {
            id: this.branch.id,
            name: this.branch.name,
            nameCanonical: this.branch.nameCanonical
          },
          companies: [],
          start: null,
          end: null,
          closureMessage: {fr: "", en: ""}
        };
        this.$modal.show("schedule-edit");
      },
      async getSchedules() {
        showSpinner();
        let response = await Core.request('GET', '/api/branch_shutdown_schedules', { branch: this.branch.id });
        hideSpinner();
        if (response) {
          this.schedules = response.data;
        }
      },
      async saveSchedule() {
        let data = JSON.parse(JSON.stringify(this.selectedSchedule));
        if (!data.start || !data.end || data.companies.length === 0) {
          toast(this.$t("error.invalid_fields"), "warning", this.$t("warning"));
          return;
        }
        showSpinner();
        let response = data.id
            ? await Core.noThrowRequest('PUT', `/api/branch_shutdown_schedules/${data.id}`, data)
            : await Core.noThrowRequest('POST', '/api/branch_shutdown_schedules', data);
        hideSpinner();

        if (response) {
          let index = this.schedules.findIndex(p => p.id === response.id);
          index !== -1
              ? this.schedules.splice(index, 1, response)
              : this.schedules.push(response);
        }
        this.$modal.hide("schedule-edit");
      },
      formatDate(date) {
        return new moment(date).format(this.dateFormat);
      },
      async loadBranchCompanies() {
        this.branchCompanies = await Core.noThrowRequest('GET', '/api/entity/company/select', { branch: this.branch.id });
      },
      editSchedule(schedule) {
        this.selectedSchedule = JSON.parse(JSON.stringify(schedule));
        this.selectedSchedule.start = moment(this.selectedSchedule.start, this.apiDateFormat).toISOString();
        this.selectedSchedule.end = moment(this.selectedSchedule.end, this.apiDateFormat).toISOString();
        this.$modal.show("schedule-edit");
      },
      async removeSchedule(index, schedule) {
        let confirm = await showConfirmation(this.$t("warning"), this.$t("scheduled_shutdown.delete_confirmation"));
        if (!confirm) {
          return;
        }
        showSpinner();
        let response = await Core.noThrowRequest('DELETE', `/api/branch_shutdown_schedules/${schedule.id}`);
        hideSpinner();
        if (response) {
          toast(this.$t('scheduled_shutdown.delete_success'), "success", this.$t("success"));
          this.schedules.splice(index, 1);
        } else {
          toast(this.$t('scheduled_shutdown.delete_error'), "error", this.$t("error.title"));
        }
      },
    }
  }
</script>

<style lang="scss" scoped>
  .table {
    width: 100%;

    tr {
      &:not(:last-child) {
        td, th {
          border-bottom: 1px solid #e6e6e6;
        }
      }
    }

    td, th {
      &.icon {
        width: 1px;
      }
      &.name {
        width: 99%;
      }
    }

    .tag {
      text-transform: initial;
      white-space: nowrap;
      margin-bottom: 5px;

      &:not(:last-child) {
        margin-right: 5px;
      }
    }

    th {
      text-align: left;
      font-size: 14px;
      padding-bottom: 16px;
    }

    td {
      padding-top: 16px;
      padding-bottom: 16px;
      &:first-child {
        padding-left: 16px;
      }
      &:last-child {
        padding-right: 16px;
      }
    }
    .actions {
      height: 32px;
      width: 100%;
    }
    .table__icon {
      height: 32px;
      width: 32px;
      display: flex;
      border-radius: 6px;
      background-color: #f5f5f5;
      align-items: center;
      justify-content: center;
      fill: #ccc;
      margin-right: 16px;
    }

    @media screen and (max-width: 500px) {
      td, th {
        &.icon {
          display: none;
        }
      }
    }
  }
</style>

<style lang="scss">
  #scheduledShutdown {
    .modal {
      .modalContainer {
        width: 600px;
      }
    }
  }
</style>